import React, { useState, useEffect } from "react";

// ─── Chart geometry ──────────────────────────────────────────────────────────
const W = 300, H = 190;
const PAD = { top: 16, right: 14, bottom: 36, left: 46 };
const CW = W - PAD.left - PAD.right;
const CH = H - PAD.top - PAD.bottom;
const X_MIN = 2.10, X_MAX = 2.70, Y_MIN = 0, Y_MAX = 80;

function toX(r) { return PAD.left + ((r - X_MIN) / (X_MAX - X_MIN)) * CW; }
function toY(v) { return PAD.top  + ((Y_MAX - v)  / (Y_MAX - Y_MIN)) * CH; }

function sigmoid(x, c, s) { return 1 / (1 + Math.exp(-s * (x - c))); }

function buildPath(fn, steps = 80) {
  return Array.from({ length: steps + 1 }, (_, i) => {
    const r = X_MIN + (i / steps) * (X_MAX - X_MIN);
    return `${i === 0 ? "M" : "L"}${toX(r).toFixed(1)},${toY(fn(r)).toFixed(1)}`;
  }).join(" ");
}

// ─── Savings variant (ascending: higher rate → more volume) ─────────────────
const SAV = {
  allCurve:         r => { const t=(r-X_MIN)/(X_MAX-X_MIN); return 6  + 56*sigmoid(t,0.50,5);  },
  sensitiveCurve:   r => { const t=(r-X_MIN)/(X_MAX-X_MIN); return 6  + 60*sigmoid(t,0.60,10); },
  insensitiveCurve: r => { const t=(r-X_MIN)/(X_MAX-X_MIN); return 18 + 8 *sigmoid(t,0.50,4);  },
  combinedCurve:    r => { const t=(r-X_MIN)/(X_MAX-X_MIN); return 10 + 60*sigmoid(t,0.50,9);  },
  productCurve:     r => { const t=(r-X_MIN)/(X_MAX-X_MIN); return 12 + 42*sigmoid(t,0.45,6);  },
  allDots: [
    {r:2.11,v:4},{r:2.13,v:18},{r:2.15,v:8},{r:2.17,v:22},{r:2.19,v:11},
    {r:2.21,v:26},{r:2.23,v:6},{r:2.25,v:15},{r:2.27,v:30},{r:2.29,v:12},
    {r:2.31,v:20},{r:2.33,v:34},{r:2.35,v:14},{r:2.37,v:28},{r:2.39,v:38},
    {r:2.41,v:18},{r:2.43,v:42},{r:2.45,v:24},{r:2.47,v:46},{r:2.49,v:30},
    {r:2.51,v:50},{r:2.53,v:32},{r:2.55,v:56},{r:2.57,v:36},{r:2.59,v:52},
    {r:2.61,v:44},{r:2.63,v:62},{r:2.65,v:40},{r:2.67,v:58},{r:2.69,v:48},
    {r:2.70,v:64},
  ],
  sensitiveDots: [
    {r:2.12,v:8},{r:2.18,v:10},{r:2.22,v:9},{r:2.28,v:12},{r:2.32,v:10},
    {r:2.38,v:16},{r:2.42,v:22},{r:2.48,v:34},{r:2.52,v:44},{r:2.58,v:54},
    {r:2.65,v:62},{r:2.70,v:66},
  ],
  insensitiveDots: [
    {r:2.15,v:20},{r:2.20,v:22},{r:2.25,v:19},{r:2.30,v:23},{r:2.35,v:21},
    {r:2.40,v:24},{r:2.45,v:22},{r:2.50,v:25},{r:2.55,v:23},{r:2.60,v:26},
    {r:2.68,v:24},{r:2.70,v:25},
  ],
};

// ─── Mortgage variant (descending: higher rate → fewer applications) ────────
const MTG = {
  allCurve:         r => { const t=(r-X_MIN)/(X_MAX-X_MIN); return 62 - 56*sigmoid(t,0.50,5);  },
  sensitiveCurve:   r => { const t=(r-X_MIN)/(X_MAX-X_MIN); return 66 - 60*sigmoid(t,0.50,12); },
  insensitiveCurve: r => { const t=(r-X_MIN)/(X_MAX-X_MIN); return 26 - 8 *sigmoid(t,0.50,4);  },
  combinedCurve:    r => { const t=(r-X_MIN)/(X_MAX-X_MIN); return 70 - 60*sigmoid(t,0.50,9);  },
  productCurve:     r => { const t=(r-X_MIN)/(X_MAX-X_MIN); return 54 - 42*sigmoid(t,0.55,6);  },
  allDots: [
    {r:2.11,v:64},{r:2.13,v:48},{r:2.15,v:58},{r:2.17,v:42},{r:2.19,v:56},
    {r:2.21,v:40},{r:2.23,v:62},{r:2.25,v:52},{r:2.27,v:36},{r:2.29,v:54},
    {r:2.31,v:46},{r:2.33,v:32},{r:2.35,v:50},{r:2.37,v:38},{r:2.39,v:28},
    {r:2.41,v:48},{r:2.43,v:24},{r:2.45,v:42},{r:2.47,v:20},{r:2.49,v:36},
    {r:2.51,v:16},{r:2.53,v:34},{r:2.55,v:10},{r:2.57,v:30},{r:2.59,v:14},
    {r:2.61,v:22},{r:2.63,v:4},{r:2.65,v:26},{r:2.67,v:8},{r:2.69,v:18},
    {r:2.70,v:2},
  ],
  sensitiveDots: [
    {r:2.12,v:66},{r:2.18,v:62},{r:2.22,v:64},{r:2.28,v:56},{r:2.32,v:58},
    {r:2.38,v:46},{r:2.42,v:34},{r:2.48,v:18},{r:2.52,v:10},{r:2.58,v:8},
    {r:2.65,v:4},{r:2.70,v:2},
  ],
  insensitiveDots: [
    {r:2.15,v:26},{r:2.20,v:24},{r:2.25,v:27},{r:2.30,v:23},{r:2.35,v:25},
    {r:2.40,v:22},{r:2.45,v:24},{r:2.50,v:21},{r:2.55,v:23},{r:2.60,v:20},
    {r:2.68,v:22},{r:2.70,v:21},
  ],
};

// Pre-build paths for each variant
function buildVariantPaths(v) {
  return {
    pathAll:         buildPath(v.allCurve),
    pathSensitive:   buildPath(v.sensitiveCurve),
    pathInsensitive: buildPath(v.insensitiveCurve),
    pathCombined:    buildPath(v.combinedCurve),
    pathProduct:     buildPath(v.productCurve),
  };
}
const SAV_PATHS = buildVariantPaths(SAV);
const MTG_PATHS = buildVariantPaths(MTG);

const xTicks = [2.10, 2.30, 2.50, 2.70];
const yTicks = [0, 40, 80];

const FONT = "'Inter','Segoe UI',sans-serif";

// ─── Sub-components ──────────────────────────────────────────────────────────
function ChartSVG({ dots=[], curvePath, curveColor, dotColor, showDots=true, extraPath, extraColor }) {
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", display:"block" }}>
      <rect width={W} height={H} fill="#fff"/>
      {yTicks.map(v=><line key={`yg${v}`} x1={PAD.left} y1={toY(v)} x2={PAD.left+CW} y2={toY(v)} stroke="#f1f5f9" strokeWidth="1"/>)}
      {xTicks.map(v=><line key={`xg${v}`} x1={toX(v)} y1={PAD.top} x2={toX(v)} y2={PAD.top+CH} stroke="#f1f5f9" strokeWidth="1"/>)}
      <line x1={PAD.left} y1={PAD.top+CH} x2={PAD.left+CW} y2={PAD.top+CH} stroke="#e2e8f0" strokeWidth="1"/>
      <line x1={PAD.left} y1={PAD.top}    x2={PAD.left}    y2={PAD.top+CH} stroke="#e2e8f0" strokeWidth="1"/>
      {xTicks.map(v=>(
        <text key={`xt${v}`} x={toX(v)} y={PAD.top+CH+13} textAnchor="middle" fontFamily={FONT} fontSize="8" fill="#94a3b8">{v.toFixed(2)}%</text>
      ))}
      <text x={PAD.left+CW/2} y={H-2} textAnchor="middle" fontFamily={FONT} fontSize="10" fontWeight="600" fill="#64748b">Rate</text>
      {yTicks.map(v=>(
        <text key={`yt${v}`} x={PAD.left-5} y={toY(v)+3} textAnchor="end" fontFamily={FONT} fontSize="8" fill="#94a3b8">£{v}m</text>
      ))}
      <text
        x="12"
        y={PAD.top + CH / 2}
        transform={`rotate(-90 12 ${PAD.top + CH / 2})`}
        textAnchor="middle"
        fontFamily={FONT}
        fontSize="10"
        fontWeight="600"
        fill="#64748b"
      >
        Balance
      </text>
      {showDots && dots.map((d,i)=>(
        <circle key={i} cx={toX(d.r)} cy={toY(d.v)} r="3.5" fill={dotColor} opacity="0.75"/>
      ))}
      {extraPath && (
        <path d={extraPath} fill="none" stroke={extraColor} strokeWidth="2.5" strokeLinecap="round"/>
      )}
      {curvePath && (
        <path d={curvePath} fill="none" stroke={curveColor} strokeWidth="2.5" strokeLinecap="round"/>
      )}
    </svg>
  );
}

function ChartCard({ label, labelColor, r2, r2Color, r2Bg, r2Border, children }) {
  return (
    <div style={{
      background:"#fff", borderRadius:"12px",
      boxShadow:"0 4px 20px rgba(0,0,0,0.08)", overflow:"hidden",
    }}>
      <div style={{ padding:"9px 12px 3px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <span style={{ fontSize:"11px", fontWeight:700, color:labelColor, fontFamily:FONT }}>{label}</span>
        {r2 && (
          <span style={{
            fontSize:"10px", fontWeight:700, color:r2Color, fontFamily:FONT,
            background:r2Bg||"#f8fafc", border:`1px solid ${r2Border||"#e2e8f0"}`,
            borderRadius:"4px", padding:"1px 6px",
          }}>{r2}</span>
        )}
      </div>
      {children}
    </div>
  );
}

// ─── Copy per frame ─────────────────────────────────────────────────────────
const FRAMES = [
  {
    stepBg:"#f1f5f9", stepColor:"#64748b",
    title:"Single model, all customers",
    body:"A blended model gives a reasonable fit but misses the true structure hidden in your data.",
    badges:[{label:"R² = 0.7", bg:"#f1f5f9", border:"#e2e8f0", color:"#94a3b8"}],
  },
  {
    stepBg:"#eff6ff", stepColor:"#2563eb",
    title:"Segment, then model separately",
    body:"Rate-sensitive and non rate-sensitive customers behave very differently. Separate models capture this precisely.",
    badges:[
      {label:"Rate-sensitive R² = 0.9",    bg:"#eff6ff", border:"#bfdbfe", color:"#2563eb"},
      {label:"Non rate-sensitive R² = 0.9", bg:"#fff1f2", border:"#fecdd3", color:"#be123c"},
    ],
  },
  {
    stepBg:"#f0fdf4", stepColor:"#16a34a",
    title:"Recombine for a superior fit",
    body:"Combining segment models back to product level dramatically outperforms the blended approach.",
    badges:[{label:"✓ Combined R² = 0.9", bg:"#f0fdf4", border:"#bbf7d0", color:"#16a34a"}],
  },
];

// ─── Main component ─────────────────────────────────────────────────────────
export default function SegmentationModal({ isOpen, onClose, variant = 'savings' }) {
  const [frame, setFrame] = useState(0); // 0, 1, 2
  const [headerOffset, setHeaderOffset] = useState(96);
  const isMtg = variant === 'mortgage';
  const data  = isMtg ? MTG : SAV;
  const paths = isMtg ? MTG_PATHS : SAV_PATHS;

  // Reset to first frame when modal opens
  useEffect(() => {
    if (isOpen) {
      setFrame(0);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Keep modal/backdrop below the fixed site header.
  useEffect(() => {
    const updateHeaderOffset = () => {
      const headerEl = document.querySelector('.header');
      if (headerEl) {
        const nextOffset = Math.ceil(headerEl.getBoundingClientRect().height);
        setHeaderOffset(nextOffset > 0 ? nextOffset : 96);
      } else {
        setHeaderOffset(96);
      }
    };

    updateHeaderOffset();
    window.addEventListener('resize', updateHeaderOffset);
    return () => window.removeEventListener('resize', updateHeaderOffset);
  }, []);

  if (!isOpen) return null;

  const c = FRAMES[frame];
  const isStepTwo = frame === 1;
  const NARRATIVE_W = 185;
  const CHART_COL_W = 310;
  const ARROW_W     = 60;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', top: `${headerOffset}px`, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
          zIndex: 9998,
        }}
      />

      {/* Modal */}
      <div style={{
        position: 'fixed', top: `${headerOffset + 12}px`, left: '50%',
        transform: 'translateX(-50%)',
        width: '92vw', maxWidth: '1060px',
        height: `calc(100vh - ${headerOffset + 24}px)`, maxHeight: '720px',
        background: '#f8fafc', borderRadius: '16px',
        boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
        zIndex: 9999, overflow: 'hidden',
        fontFamily: FONT,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '24px',
        boxSizing: 'border-box',
        gap: '16px',
      }}>

        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close segmentation explainer"
          title="Close"
          style={{
            position: 'absolute', top: '16px', right: '16px',
            width: '36px', height: '36px',
            background: '#fff', border: '1px solid #e2e8f0',
            borderRadius: '8px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            transition: 'background 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
          onMouseLeave={e => e.currentTarget.style.background = '#fff'}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#334155" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="3" x2="13" y2="13" />
            <line x1="13" y1="3" x2="3" y2="13" />
          </svg>
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center' }}>
          <h2 style={{
            fontFamily: FONT,
            fontSize: '24px', fontWeight: 700, color: '#0f172a', marginBottom: '4px',
          }}>
            Understanding your customers
          </h2>
          <p style={{ fontSize: '14px', color: '#64748b', maxWidth: '420px', lineHeight: 1.6, margin: '0 auto' }}>
            Price sensitivity varies across your book. Modelling it separately gives far more accurate predictions.
          </p>
        </div>

        {/* Main content area */}
        <div style={{
          display: 'flex', gap: isStepTwo ? '14px' : '20px',
          alignItems: isStepTwo ? 'flex-start' : 'center',
          justifyContent: 'center',
          width: '100%',
          maxWidth: '960px',
          minHeight: 0,
          flex: '1 1 auto',
          overflow: 'hidden',
        }}>

          {/* Left: Narrative card */}
          <div style={{
            width: `${NARRATIVE_W}px`, minWidth: `${NARRATIVE_W}px`,
            background: '#fff', borderRadius: '14px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
            padding: '18px',
            display: 'flex', flexDirection: 'column', gap: '8px',
            alignSelf: 'center',
          }}>
            <div style={{
              display: 'inline-block', background: c.stepBg, color: c.stepColor,
              borderRadius: '4px', padding: '2px 8px',
              fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', alignSelf: 'flex-start',
              transition: 'all 0.3s ease',
            }}>
              Step 0{frame + 1}
            </div>
            <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>
              {c.title}
            </h3>
            <p style={{ fontSize: '11px', color: '#64748b', lineHeight: 1.65 }}>
              {c.body}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '4px' }}>
              {c.badges.map((b, i) => (
                <div key={i} style={{
                  background: b.bg, border: `1px solid ${b.border}`, color: b.color,
                  borderRadius: '5px', padding: '3px 9px',
                  fontSize: '10px', fontWeight: 600, alignSelf: 'flex-start',
                }}>{b.label}</div>
              ))}
            </div>
          </div>

          {/* Right: Charts — conditional per frame */}
          <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', alignItems: 'center', gap: '16px' }}>

            {/* ── Frame 0: Single blended chart ── */}
            {frame === 0 && (
              <div style={{ width: `${CHART_COL_W}px` }}>
                <p style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '6px', fontWeight: 500 }}>
                  All customers — product-level model
                </p>
                <ChartCard label="Product-level" labelColor="#94a3b8" r2="R² = 0.7" r2Color="#94a3b8">
                  <ChartSVG dots={data.allDots} curvePath={paths.pathAll} curveColor="#94a3b8" dotColor="#94a3b8"/>
                </ChartCard>
              </div>
            )}

            {/* ── Frame 1: Segmented pair stacked ── */}
            {frame === 1 && (
              <div style={{
                width: `${CHART_COL_W}px`,
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                transform: 'scale(0.92)',
                transformOrigin: 'top center',
              }}>
                <div>
                  <ChartCard label="Rate-sensitive" labelColor="#2563eb" r2="R² = 0.9" r2Color="#2563eb" r2Bg="#eff6ff" r2Border="#bfdbfe">
                    <ChartSVG dots={data.sensitiveDots} curvePath={paths.pathSensitive} curveColor="#2563eb" dotColor="#2563eb"/>
                  </ChartCard>
                </div>
                <div>
                  <ChartCard label="Non rate-sensitive" labelColor="#be123c" r2="R² = 0.9" r2Color="#be123c" r2Bg="#fff1f2" r2Border="#fecdd3">
                    <ChartSVG dots={data.insensitiveDots} curvePath={paths.pathInsensitive} curveColor="#be123c" dotColor="#be123c"/>
                  </ChartCard>
                </div>
              </div>
            )}

            {/* ── Frame 2: Segmented pair (faded) → arrow → combined ── */}
            {frame === 2 && (
              <>
                {/* Faded segment charts */}
                <div style={{ width: `${CHART_COL_W}px`, opacity: 0.35, display: 'flex', flexDirection: 'column', gap: '10px', flexShrink: 0 }}>
                  <div>
                    <p style={{ fontSize: '10px', color: '#2563eb', marginBottom: '5px', fontWeight: 600, opacity: 0 }}>_</p>
                    <ChartCard label="Rate-sensitive" labelColor="#2563eb" r2="R² = 0.9" r2Color="#2563eb" r2Bg="#eff6ff" r2Border="#bfdbfe">
                      <ChartSVG dots={data.sensitiveDots} curvePath={paths.pathSensitive} curveColor="#2563eb" dotColor="#2563eb"/>
                    </ChartCard>
                  </div>
                  <div>
                    <p style={{ fontSize: '10px', color: '#be123c', marginBottom: '5px', fontWeight: 600, opacity: 0 }}>_</p>
                    <ChartCard label="Non rate-sensitive" labelColor="#be123c" r2="R² = 0.9" r2Color="#be123c" r2Bg="#fff1f2" r2Border="#fecdd3">
                      <ChartSVG dots={data.insensitiveDots} curvePath={paths.pathInsensitive} curveColor="#be123c" dotColor="#be123c"/>
                    </ChartCard>
                  </div>
                </div>

                {/* Arrow */}
                <div style={{
                  width: `${ARROW_W}px`, flexShrink: 0,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                }}>
                  <svg width="40" height="40" viewBox="0 0 40 40">
                    <defs>
                      <linearGradient id="segArrowGrad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#2563eb"/>
                        <stop offset="100%" stopColor="#16a34a"/>
                      </linearGradient>
                    </defs>
                    <path d="M2 20 H32 M23 12 L34 20 L23 28"
                      stroke="url(#segArrowGrad)" strokeWidth="2.5"
                      fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span style={{ fontSize: '9px', color: '#64748b', fontWeight: 600 }}>Combine</span>
                </div>

                {/* Combined chart + comparison */}
                <div style={{
                  width: `${CHART_COL_W}px`, flexShrink: 0,
                  display: 'flex', flexDirection: 'column', gap: '8px',
                }}>
                  <p style={{ fontSize: '10px', color: '#16a34a', marginBottom: '2px', fontWeight: 600 }}>
                    Product-level model
                  </p>
                  <ChartCard
                    label="Combined model" labelColor="#0f172a"
                    r2="R² = 0.9" r2Color="#16a34a" r2Bg="#f0fdf4" r2Border="#bbf7d0"
                  >
                    <ChartSVG
                      showDots={false}
                      curvePath={paths.pathCombined}
                      curveColor="#0f172a"
                      extraPath={paths.pathProduct}
                      extraColor="#94a3b8"
                    />
                  </ChartCard>

                  {/* Comparison badge */}
                  <div style={{
                    background: '#f8fafc', border: '1px solid #e2e8f0',
                    borderRadius: '8px', padding: '8px 12px',
                    display: 'flex', flexDirection: 'column', gap: '6px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ width: '16px', height: '2.5px', background: '#94a3b8', borderRadius: '2px' }}/>
                        <span style={{ fontSize: '10px', color: '#64748b', fontWeight: 500 }}>Product-level only</span>
                      </div>
                      <span style={{
                        fontSize: '10px', fontWeight: 700, color: '#64748b',
                        background: '#f1f5f9', border: '1px solid #e2e8f0',
                        borderRadius: '4px', padding: '1px 6px',
                      }}>R² = 0.7</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ width: '16px', height: '2.5px', background: '#0f172a', borderRadius: '2px' }}/>
                        <span style={{ fontSize: '10px', color: '#0f172a', fontWeight: 600 }}>Combined model</span>
                      </div>
                      <span style={{
                        fontSize: '10px', fontWeight: 700, color: '#16a34a',
                        background: '#f0fdf4', border: '1px solid #bbf7d0',
                        borderRadius: '4px', padding: '1px 6px',
                      }}>↑ R² = 0.9</span>
                    </div>
                    <div style={{
                      paddingTop: '5px', borderTop: '1px solid #e2e8f0',
                      fontSize: '10px', color: '#16a34a', fontWeight: 600, textAlign: 'center',
                    }}>
                      ✓ Segment models outperform product-level fit
                    </div>
                  </div>
                </div>
              </>
            )}

          </div>
        </div>

        {/* Navigation: dots + arrows */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
          {/* Back button */}
          <button
            onClick={() => setFrame(f => Math.max(0, f - 1))}
            disabled={frame === 0}
            style={{
              width: '36px', height: '36px',
              borderRadius: '50%', border: '1px solid #e2e8f0',
              background: frame === 0 ? '#f8fafc' : '#fff',
              cursor: frame === 0 ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: frame === 0 ? 0.4 : 1,
              transition: 'all 0.2s ease',
              boxShadow: frame === 0 ? 'none' : '0 2px 8px rgba(0,0,0,0.06)',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 2L4 7L9 12" />
            </svg>
          </button>

          {/* Phase dots */}
          <div style={{ display: 'flex', gap: '7px' }}>
            {[0,1,2].map(i => (
              <button
                key={i}
                onClick={() => setFrame(i)}
                style={{
                  width: frame === i ? '20px' : '8px', height: '8px',
                  borderRadius: '4px', transition: 'all 0.4s ease',
                  background: frame === i ? '#2563eb' : '#cbd5e1',
                  border: 'none', cursor: 'pointer', padding: 0,
                }}
              />
            ))}
          </div>

          {/* Next button */}
          <button
            onClick={() => setFrame(f => Math.min(2, f + 1))}
            disabled={frame === 2}
            style={{
              width: '36px', height: '36px',
              borderRadius: '50%', border: '1px solid #e2e8f0',
              background: frame === 2 ? '#f8fafc' : '#fff',
              cursor: frame === 2 ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: frame === 2 ? 0.4 : 1,
              transition: 'all 0.2s ease',
              boxShadow: frame === 2 ? 'none' : '0 2px 8px rgba(0,0,0,0.06)',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 2L10 7L5 12" />
            </svg>
          </button>
        </div>

      </div>
    </>
  );
}
